import { useState, useEffect, useCallback } from 'react';
import { Lock, TrendingUp, PieChart, Layers, RefreshCw, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const API_BASE = 'https://tickertapapi.avetter90.workers.dev';

// Known ETF tickers — used to filter portfolio for selector
const ETF_INDICATORS = ['JEPQ', 'QYLD', 'JEPI', 'XYLD', 'RYLD', 'DIVO', 'SCHD', 'VYM', 'HDV', 'DVY',
  'SPHD', 'SPYD', 'VIG', 'NOBL', 'SDY', 'FVD', 'PFF', 'PFFD', 'EMLC', 'HYG',
  'JNK', 'VCLT', 'BND', 'AGG', 'LQD', 'MUB', 'TLT', 'IEF', 'SHY', 'ABTC'];

// Sector color palette — deterministic by sector name
const SECTOR_COLORS = [
  '#22d3ee', '#f59e0b', '#a78bfa', '#34d399', '#f87171',
  '#60a5fa', '#fb923c', '#e879f9', '#4ade80', '#facc15',
  '#38bdf8', '#c084fc'
];

function getSectorColor(index) {
  return SECTOR_COLORS[index % SECTOR_COLORS.length];
}

// ---- SECTOR BAR ----
function SectorBar({ sectors }) {
  const [hovered, setHovered] = useState(null);
  if (!sectors || sectors.length === 0) return null;

  const top = sectors.slice(0, 10);
  const otherWeight = sectors.slice(10).reduce((s, x) => s + x.weight, 0);
  const displayed = otherWeight > 0.1 ? [...top, { sector: 'Other', weight: parseFloat(otherWeight.toFixed(2)) }] : top;
  const total = displayed.reduce((s, x) => s + x.weight, 0);

  return (
    <div>
      {/* Stacked bar */}
      <div className="flex h-8 rounded-lg overflow-hidden mb-4 border border-[#1a1a1a]">
        {displayed.map((s, i) => (
          <div
            key={s.sector}
            style={{
              width: `${(s.weight / total) * 100}%`,
              backgroundColor: getSectorColor(i),
              opacity: hovered === null || hovered === i ? 1 : 0.3,
              transition: 'opacity 0.15s'
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            title={`${s.sector}: ${s.weight.toFixed(1)}%`}
          />
        ))}
      </div>

      {/* Legend grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        {displayed.map((s, i) => (
          <div
            key={s.sector}
            className="flex items-center justify-between cursor-default"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: getSectorColor(i) }}
              />
              <span
                className="text-xs truncate"
                style={{
                  color: hovered === i ? '#e5e7eb' : '#9ca3af',
                  transition: 'color 0.15s'
                }}
              >
                {s.sector}
              </span>
            </div>
            <span className="text-xs font-mono ml-2 flex-shrink-0"
              style={{ color: hovered === i ? '#e5e7eb' : '#6b7280' }}>
              {s.weight.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- CONCENTRATION METER ----
function ConcentrationMeter({ value, holdingsCount }) {
  const pct = Math.min(value, 100);
  let color = '#22d3ee';
  let label = 'Diversified';
  if (pct > 60) { color = '#f59e0b'; label = 'Moderate'; }
  if (pct > 80) { color = '#f87171'; label = 'Concentrated'; }

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-gray-500">Top 10 Concentration</span>
        <span className="text-xs font-mono" style={{ color }}>{label}</span>
      </div>
      <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden mb-1">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <div className="flex justify-between">
        <span className="text-xs font-mono" style={{ color }}>{value.toFixed(1)}%</span>
        <span className="text-xs text-gray-600">{holdingsCount} total holdings</span>
      </div>
    </div>
  );
}

// ---- HOLDINGS TABLE ----
function HoldingsTable({ holdings }) {
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('weight');

  const sorted = [...holdings].sort((a, b) => {
    if (sortBy === 'weight') return b.weight - a.weight;
    if (sortBy === 'name') return (a.symbol || '').localeCompare(b.symbol || '');
    return 0;
  });

  const visible = expanded ? sorted : sorted.slice(0, 15);
  const maxWeight = sorted[0]?.weight || 1;

  return (
    <div>
      {/* Sort controls */}
      <div className="flex gap-3 mb-3">
        <button
          onClick={() => setSortBy('weight')}
          className="text-xs px-2 py-1 rounded transition-colors"
          style={{
            background: sortBy === 'weight' ? '#1a1a1a' : 'transparent',
            color: sortBy === 'weight' ? '#22d3ee' : '#6b7280',
            border: sortBy === 'weight' ? '1px solid #22d3ee33' : '1px solid transparent'
          }}
        >
          By Weight
        </button>
        <button
          onClick={() => setSortBy('name')}
          className="text-xs px-2 py-1 rounded transition-colors"
          style={{
            background: sortBy === 'name' ? '#1a1a1a' : 'transparent',
            color: sortBy === 'name' ? '#22d3ee' : '#6b7280',
            border: sortBy === 'name' ? '1px solid #22d3ee33' : '1px solid transparent'
          }}
        >
          By Symbol
        </button>
      </div>

      {/* Table */}
      <div className="space-y-1">
        {visible.map((h, i) => (
          <div
            key={h.symbol || i}
            className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-[#111] transition-colors group"
          >
            {/* Rank */}
            <span className="text-xs text-gray-700 w-5 text-right flex-shrink-0 font-mono">{i + 1}</span>

            {/* Symbol + name */}
            <div className="flex flex-col min-w-0 w-28 flex-shrink-0">
              <span className="text-xs font-mono text-gray-200 truncate">{h.symbol || '—'}</span>
              {h.name && (
                <span className="text-xs text-gray-600 truncate leading-tight">{h.name}</span>
              )}
            </div>

            {/* Weight bar */}
            <div className="flex-1 min-w-0">
              <div className="h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${(h.weight / maxWeight) * 100}%`,
                    backgroundColor: i < 3 ? '#22d3ee' : i < 10 ? '#22d3ee88' : '#22d3ee33'
                  }}
                />
              </div>
            </div>

            {/* Weight % */}
            <span className="text-xs font-mono w-12 text-right flex-shrink-0"
              style={{ color: i < 3 ? '#22d3ee' : i < 10 ? '#9ca3af' : '#6b7280' }}>
              {h.weight.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>

      {/* Expand/collapse */}
      {holdings.length > 15 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs text-gray-500 hover:text-gray-300 hover:bg-[#111] transition-colors border border-[#1a1a1a]"
        >
          {expanded ? (
            <><ChevronUp className="w-3 h-3" />Show less</>
          ) : (
            <><ChevronDown className="w-3 h-3" />Show all {holdings.length} holdings</>
          )}
        </button>
      )}
    </div>
  );
}

// ---- STAT PILL ----
function StatPill({ label, value, accent }) {
  return (
    <div className="flex flex-col gap-0.5 px-4 py-3 rounded-lg border border-[#1a1a1a] bg-[#0f0f0f]">
      <span className="text-xs text-gray-600 uppercase tracking-wider">{label}</span>
      <span className="text-sm font-mono font-semibold" style={{ color: accent || '#e5e7eb' }}>{value}</span>
    </div>
  );
}

// ---- PRO LOCK OVERLAY ----
function ProLockOverlay({ onUpgrade }) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl"
      style={{ background: 'rgba(10,10,10,0.85)', backdropFilter: 'blur(4px)' }}>
      <div className="text-center max-w-xs px-6">
        <div className="w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center mx-auto mb-4">
          <Lock className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm font-semibold text-gray-200 mb-1">Pro Feature</p>
        <p className="text-xs text-gray-500 mb-4">ETF X-Ray requires a Pro subscription to view holdings breakdowns and sector allocations.</p>
        <button
          onClick={onUpgrade}
          className="px-5 py-2 rounded-lg text-xs font-semibold text-black transition-opacity hover:opacity-90"
          style={{ background: '#22d3ee' }}
        >
          Upgrade to Pro
        </button>
      </div>
    </div>
  );
}

// ---- TICKER SELECTOR ----
function TickerSelector({ tickers, selected, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {tickers.map(t => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className="px-3 py-1.5 rounded-lg text-xs font-mono font-semibold transition-all"
          style={{
            background: selected === t ? '#22d3ee' : '#0f0f0f',
            color: selected === t ? '#000' : '#9ca3af',
            border: selected === t ? '1px solid #22d3ee' : '1px solid #1a1a1a'
          }}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function ETFXRay({ session, userTier, portfolioTickers = [], onUpgrade }) {
  const isPro = false; // Coming soon — data source pending
  const isComingSoon = true;

  // Determine which tickers are ETFs from portfolio
  const etfTickers = portfolioTickers.filter(t =>
    ETF_INDICATORS.includes(t.toUpperCase())
  );

  // Fallback: if no ETFs detected in portfolio, show JEPQ + QYLD as demo
  const availableTickers = etfTickers.length > 0 ? etfTickers : ['JEPQ', 'QYLD'];

  const [selected, setSelected] = useState(availableTickers[0] || 'JEPQ');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = useCallback(async (ticker) => {
    if (!session?.access_token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/etf/holdings?symbol=${ticker}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (isPro && selected) fetchData(selected);
  }, [isPro, selected, fetchData]);

  const handleTickerChange = (t) => {
    setSelected(t);
    setData(null);
  };

  const formatAUM = (aum) => {
    if (!aum) return '—';
    if (aum >= 1e9) return `$${(aum / 1e9).toFixed(1)}B`;
    if (aum >= 1e6) return `$${(aum / 1e6).toFixed(0)}M`;
    return `$${aum.toLocaleString()}`;
  };

  if (isComingSoon) {
    return (
      <div className="p-6 flex flex-col items-center justify-center" style={{ background: '#0a0a0a', minHeight: '80vh' }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-[#0f0f0f] border border-[#1a1a1a] flex items-center justify-center mx-auto mb-6">
            <Layers className="w-7 h-7" style={{ color: '#22d3ee' }} />
          </div>
          <div className="flex items-center gap-2 justify-center mb-3">
            <h2 className="text-lg font-semibold text-gray-100">ETF Lookthrough</h2>
          </div>
          <p className="text-xs text-gray-500 mb-1 leading-relaxed">
            See exactly what's inside your <span className="text-gray-400">Exchange-Traded Funds</span> and <span className="text-gray-400">Mutual Funds</span> — top holdings, sector allocation, and overlap across your entire portfolio.
          </p>
          <p className="text-xs text-gray-600 mb-6 leading-relaxed">
            Know if you're actually diversified or just holding the same 10 stocks five different ways.
          </p>
          <div className="space-y-2 mb-8">
            {['ETF & mutual fund holdings breakdown with % weight', 'Sector allocation visualized', 'Cross-fund overlap detector', 'Concentration risk score across all positions'].map(f => (
              <div key={f} className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#22d3ee' }} />
                {f}
              </div>
            ))}
          </div>
          <span className="inline-block px-4 py-2 rounded-lg text-xs font-semibold text-gray-500 border border-[#2a2a2a] bg-[#0f0f0f]">
            Coming Soon
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" style={{ background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="w-4 h-4" style={{ color: '#22d3ee' }} />
            <h1 className="text-base font-semibold text-gray-100">ETF X-Ray</h1>
            {!isPro && (
              <span className="text-xs px-2 py-0.5 rounded font-semibold"
                style={{ background: '#1a1a1a', color: '#f59e0b', border: '1px solid #f59e0b44' }}>
                PRO
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">Holdings breakdown and sector exposure for your ETF positions</p>
        </div>

        {isPro && lastFetched && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <RefreshCw className="w-3 h-3" />
            {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* Ticker selector */}
      <div>
        <p className="text-xs text-gray-600 mb-2 uppercase tracking-wider">Select ETF</p>
        <TickerSelector
          tickers={availableTickers}
          selected={selected}
          onChange={handleTickerChange}
        />
      </div>

      {/* Main panel — relative so Pro lock can overlay */}
      <div className="relative">

        {/* Pro lock overlay */}
        {!isPro && <ProLockOverlay onUpgrade={onUpgrade} />}

        {/* Loading state */}
        {isPro && loading && (
          <div className="flex items-center justify-center py-16">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Loading {selected} holdings...
            </div>
          </div>
        )}

        {/* Error state */}
        {isPro && error && !loading && (
          <div className="flex items-center gap-2 py-8 px-4 rounded-lg border border-[#1a1a1a]"
            style={{ background: '#0f0f0f' }}>
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-xs text-red-400">{error}</span>
          </div>
        )}

        {/* Data loaded */}
        {(isPro ? data : true) && !loading && !error && (
          <div className="space-y-4">

            {/* Blur placeholder for free tier */}
            {!isPro && (
              <div className="space-y-4" style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
                <DemoPlaceholder />
              </div>
            )}

            {/* Real data for Pro */}
            {isPro && data && (
              <>
                {/* Stats row */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatPill label="ETF" value={data.symbol} accent="#22d3ee" />
                  <StatPill label="Holdings" value={data.holdingsCount || '—'} />
                  <StatPill label="AUM" value={formatAUM(data.aum)} />
                  <StatPill
                    label="Expense Ratio"
                    value={data.expenseRatio ? `${(data.expenseRatio * 100).toFixed(2)}%` : '—'}
                  />
                </div>

                {/* Concentration meter */}
                <div className="rounded-xl border border-[#1a1a1a] p-4" style={{ background: '#0f0f0f' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Concentration Risk</span>
                  </div>
                  <ConcentrationMeter
                    value={data.top10Concentration}
                    holdingsCount={data.holdingsCount}
                  />
                </div>

                {/* Two-column layout: Sectors + Holdings */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

                  {/* Sector allocation */}
                  <div className="rounded-xl border border-[#1a1a1a] p-4" style={{ background: '#0f0f0f' }}>
                    <div className="flex items-center gap-2 mb-4">
                      <PieChart className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sector Allocation</span>
                    </div>
                    {data.sectors && data.sectors.length > 0
                      ? <SectorBar sectors={data.sectors} />
                      : <p className="text-xs text-gray-600 py-4 text-center">No sector data available</p>
                    }
                  </div>

                  {/* Top holdings */}
                  <div className="rounded-xl border border-[#1a1a1a] p-4" style={{ background: '#0f0f0f' }}>
                    <div className="flex items-center gap-2 mb-4">
                      <Layers className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Top Holdings</span>
                    </div>
                    {data.holdings && data.holdings.length > 0
                      ? <HoldingsTable holdings={data.holdings} />
                      : <p className="text-xs text-gray-600 py-4 text-center">No holdings data available</p>
                    }
                  </div>
                </div>

                {/* Footer timestamp */}
                <p className="text-xs text-gray-700 text-right">
                  Data as of {new Date(data.asOf).toLocaleDateString()} · Cached 4hr
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- DEMO PLACEHOLDER (blurred behind lock for free tier) ----
function DemoPlaceholder() {
  const fakeSectors = [
    { sector: 'Technology', weight: 31.2 },
    { sector: 'Financials', weight: 18.5 },
    { sector: 'Healthcare', weight: 13.1 },
    { sector: 'Consumer Disc.', weight: 10.8 },
    { sector: 'Industrials', weight: 8.4 },
    { sector: 'Other', weight: 18.0 },
  ];
  const fakeHoldings = [
    { symbol: 'MSFT', name: 'Microsoft Corp', weight: 7.2 },
    { symbol: 'AAPL', name: 'Apple Inc', weight: 6.8 },
    { symbol: 'NVDA', name: 'NVIDIA Corp', weight: 5.9 },
    { symbol: 'AMZN', name: 'Amazon.com Inc', weight: 3.4 },
    { symbol: 'META', name: 'Meta Platforms', weight: 2.8 },
  ];
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {['JEPQ', '85', '$12.4B', '0.35%'].map((v, i) => (
          <div key={i} className="px-4 py-3 rounded-lg border border-[#1a1a1a] bg-[#0f0f0f]">
            <div className="h-2 w-12 bg-[#1a1a1a] rounded mb-2" />
            <span className="text-sm font-mono text-gray-300">{v}</span>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-[#1a1a1a] p-4 bg-[#0f0f0f]">
        <SectorBar sectors={fakeSectors} />
      </div>
      <div className="rounded-xl border border-[#1a1a1a] p-4 bg-[#0f0f0f]">
        <HoldingsTable holdings={fakeHoldings} />
      </div>
    </>
  );
}
